// Le choix du rythme des mails (lot 2 de la refonte). Sert deux fois : à la
// troisième étape de l'inscription et dans les Réglages. Un seul composant pour
// que les deux disent la même chose avec les mêmes chiffres.
//
// ON NE RÈGLE PLUS UN SEUIL : on choisit combien on veut être dérangé. Chaque
// préréglage porte son nombre d'offres réel sur les 30 derniers jours, calculé
// par le serveur sur les sites du compte (`estimationsParMois`). Un chiffre
// inventé ou moyen tromperait précisément la personne qui choisit « Discret »
// et ne reçoit rien pendant un mois.

import { useState } from 'react'
import { Check } from 'lucide-react'
import { useT } from '../lib/i18n'
import { get, post } from '../lib/api'
import { dateHeure } from '../lib/format'
import { ActionButton, useData, useToast } from './common'
import { CONTRATS_ACCEPTES, LANGUES_PARLEES, NIVEAUX_ACCEPTES, PALIERS_SALAIRE, basculer, basculerLangue, coches, exclus, formaterMontant, languesExclues, languesParlees, lireMontant, palierDe } from '../lib/reglagesPositifs'
import { useLang } from '../lib/lang'

export type Rythme = 'discret' | 'equilibre' | 'actif'
export const RYTHMES: Rythme[] = ['discret', 'equilibre', 'actif']

export type Region = 'bruxelles' | 'wallonie' | 'flandre' | 'luxembourg' | 'etranger'
export const REGIONS: Region[] = ['bruxelles', 'wallonie', 'flandre', 'luxembourg', 'etranger']

export interface EtatRythme {
  seuil: number
  rythme: Rythme | null
  estimations: Record<Rythme, number>
  coupes: boolean
  /** Fin de la pause des mails en cours (issue #175), ou null. */
  pause?: string | null
  regions: Region[]
  /** L'adresse où Radar écrit : la confirmée, sinon celle du compte. */
  email: string | null
  emailCompte?: string | null
  /** Une nouvelle adresse qui attend son lien de confirmation. */
  enAttente?: string | null
  mails?: ReglagesMails
  exclusions?: Exclusions
}

/** Cf. `src/services/reglagesMails.ts`. */
export interface ReglagesMails {
  frequence: 'matin' | 'hebdo'
  seuilAccueil: number | null
  lundi: boolean
  relance: boolean
  prioritaires: number[]
  muets: number[]
  mots: string[]
}

/** Cf. `src/services/criteresOffre.ts`. */
export interface Exclusions {
  contrats: string[]
  surSite: boolean
  langues: string[]
  niveaux: string[]
  salaireMin: number | null
  /** Cf. TEMPS_CHERCHES : `null` peu importe. */
  temps?: 'plein' | 'partiel' | null
}

/**
 * Les critères, cochés À L'ENDROIT (issues #173 et #174) : les contrats
 * acceptés, les niveaux qui conviennent, « Je parle ». La base garde ce que le
 * profil refuse (`exclusions`, cf. services/criteresOffre.ts) : une offre qui
 * DIT un critère décoché est écartée des mails et de l'accueil, une offre qui
 * n'en dit rien passe toujours.
 */
export function ChoixExclusions({ e, onChange }: { e: Exclusions; onChange: (e: Exclusions) => void }) {
  const t = useT()
  const contrats = coches(CONTRATS_ACCEPTES, e.contrats)
  const niveaux = coches(NIVEAUX_ACCEPTES, e.niveaux)
  const parlees = languesParlees(e.langues)
  return (
    <>
      <div className="cfg-sous">
        <b>{t('excl.contrats')}</b>
        <div className="regions">
          {CONTRATS_ACCEPTES.map((c) =>
            pastille(contrats.includes(c), t(`excl.${c}` as const), () =>
              onChange({ ...e, contrats: exclus(CONTRATS_ACCEPTES, basculer(CONTRATS_ACCEPTES, contrats, c)) })
            )
          )}
        </div>
      </div>
      <div className="cfg-sous">
        <b>{t('excl.temps')}</b>
        <div className="regions" role="radiogroup">
          {([null, 'plein', 'partiel'] as const).map((v) =>
            pastille((e.temps ?? null) === v, t(v === null ? 'excl.peuImporte' : v === 'plein' ? 'excl.tempsPlein' : 'excl.tempsPartiel'), () =>
              (e.temps ?? null) !== v && onChange({ ...e, temps: v })
            )
          )}
        </div>
      </div>
      <div className="cfg-sous">
        <b>{t('excl.teletravail')}</b>
        <div className="regions" role="radiogroup">
          {pastille(!e.surSite, t('excl.peuImporte'), () => e.surSite && onChange({ ...e, surSite: false }))}
          {pastille(e.surSite, t('excl.hybride'), () => !e.surSite && onChange({ ...e, surSite: true }))}
        </div>
      </div>
      <div className="cfg-sous">
        <b>{t('excl.niveaux')}</b>
        <div className="regions">
          {NIVEAUX_ACCEPTES.map((n) =>
            pastille(niveaux.includes(n), t(`excl.${n}` as const), () =>
              onChange({ ...e, niveaux: exclus(NIVEAUX_ACCEPTES, basculer(NIVEAUX_ACCEPTES, niveaux, n)) })
            )
          )}
        </div>
      </div>
      <div className="cfg-sous">
        <b>{t('excl.jeParle')}</b>
        <div className="regions">
          {LANGUES_PARLEES.map((l) =>
            pastille(parlees.includes(l), t(`excl.parle.${l}` as const), () =>
              onChange({ ...e, langues: languesExclues(basculerLangue(parlees, l)) })
            )
          )}
        </div>
      </div>
      <ChoixSalaire montant={e.salaireMin} onChange={(salaireMin) => onChange({ ...e, salaireMin })} />
    </>
  )
}

/** Le salaire minimum en paliers, et « Autre… » pour un montant libre. */
function ChoixSalaire({ montant, onChange }: { montant: number | null; onChange: (n: number | null) => void }) {
  const t = useT()
  const lang = useLang()
  const palier = palierDe(montant)
  const [autre, setAutre] = useState(palier === 'autre')
  const [saisie, setSaisie] = useState(palier === 'autre' && montant ? formaterMontant(montant, lang) : '')
  const k = (n: number) => t('excl.k', { n: n / 1000 })
  return (
    <div className="cfg-sous">
      <b>{t('excl.salaire')}</b>
      <div className="regions" role="radiogroup">
        {pastille(palier === null && !autre, t('excl.salaireAucun'), () => { setAutre(false); if (montant !== null) onChange(null) })}
        {PALIERS_SALAIRE.map((n) =>
          pastille(palier === n && !autre, k(n), () => { setAutre(false); if (montant !== n) onChange(n) })
        )}
        {pastille(autre, t('excl.salaireAutre'), () => setAutre(true))}
      </div>
      {autre && (
        <span>
          <input
            className="input-field"
            inputMode="numeric"
            autoFocus
            style={{ width: '9em', display: 'inline-block' }}
            aria-label={t('excl.salaire')}
            value={saisie}
            placeholder={formaterMontant(45000, lang)}
            onChange={(ev) => setSaisie(ev.target.value)}
            onBlur={() => {
              const n = lireMontant(saisie)
              if (n) setSaisie(formaterMontant(n, lang))
              if (n !== montant) onChange(n)
            }}
          />{' '}{t('excl.parAn')}
        </span>
      )}
    </div>
  )
}

/** Une pastille à bascule, sur le modèle des régions. */
function pastille(on: boolean, libelle: string, clic: () => void) {
  return (
    <button key={libelle} type="button" aria-pressed={on} className={`region${on ? ' on' : ''}`} onClick={clic}>
      {on && <Check size={12} strokeWidth={3} />}
      {libelle}
    </button>
  )
}

export type ChangementMails = Partial<Pick<ReglagesMails, 'frequence' | 'seuilAccueil' | 'lundi' | 'relance' | 'mots'>>

/** Quand, et quels mails. */
export function ChoixMails({ r, onChange }: { r: ReglagesMails; onChange: (m: ChangementMails) => void }) {
  const t = useT()
  return (
    <>
      <div className="cfg-sous">
        <b>{t('mails.quand')}</b>
        <div className="regions">
          {pastille(r.frequence === 'matin', t('mails.matin'), () => onChange({ frequence: 'matin' }))}
          {pastille(r.frequence === 'hebdo', t('mails.hebdo'), () => onChange({ frequence: 'hebdo' }))}
        </div>
      </div>
      <div className="cfg-sous">
        <b>{t('mails.quels')}</b>
        <div className="regions">
          {pastille(r.lundi, t('mails.lundi'), () => onChange({ lundi: !r.lundi }))}
          {pastille(r.relance, t('mails.relance'), () => onChange({ relance: !r.relance }))}
        </div>
      </div>
    </>
  )
}

/** Le repli « Avancé » (issue #173) : le seuil de l'accueil et les mots
 *  suivis. Utiles à peu de monde, et ils brouillaient le choix du rythme. */
export function ChoixAvance({ r, seuil, onChange }: { r: ReglagesMails; seuil: number; onChange: (m: ChangementMails) => void }) {
  const t = useT()
  const [mots, setMots] = useState(r.mots.join(', '))
  return (
    <details className="cfg-avance">
      <summary>{t('reglages.avance')}</summary>
      <div className="cfg-sous">
        <b>{t('mails.accueil')}</b>
        <div className="regions">
          {pastille(r.seuilAccueil === null, t('mails.commeMails', { n: seuil }), () => onChange({ seuilAccueil: null }))}
          {[50, 60, 70, 80].filter((n) => n !== seuil).map((n) =>
            pastille(r.seuilAccueil === n, String(n), () => onChange({ seuilAccueil: n }))
          )}
        </div>
      </div>
      <div className="cfg-sous">
        <b>{t('mails.mots')}</b>
        <p className="field-help">{t('mails.motsAide')}</p>
        <input
          className="input-field"
          value={mots}
          placeholder={t('mails.motsExemple')}
          onChange={(ev) => setMots(ev.target.value)}
          onBlur={() => {
            const liste = mots.split(',').map((m) => m.trim()).filter(Boolean)
            if (liste.join(',') !== r.mots.join(',')) onChange({ mots: liste })
          }}
        />
      </div>
    </details>
  )
}

/**
 * Où l'on accepte de travailler. Aucune case cochée vaut « partout » : c'est
 * l'état de départ, et l'écran le dit plutôt que de cocher les cinq. Une offre
 * dont le lieu est inconnu passe toujours (cf. services/regions.ts) : on ne
 * cache que ce qu'on sait être ailleurs.
 */
export function ChoixRegions({ choisies, onChange }: { choisies: Region[]; onChange: (r: Region[]) => void }) {
  const t = useT()
  return (
    <div className="regions" role="group" aria-label={t('regions.aria')}>
      {REGIONS.map((r) => {
        const on = choisies.includes(r)
        return (
          <button
            key={r}
            type="button"
            aria-pressed={on}
            className={`region${on ? ' on' : ''}`}
            onClick={() => onChange(on ? choisies.filter((x) => x !== r) : [...choisies, r])}
          >
            {on && <Check size={12} strokeWidth={3} />}
            {t(`regions.${r}` as const)}
          </button>
        )
      })}
    </div>
  )
}

export function ChoixRythme({
  etat,
  choisi,
  onChoisir,
  disabled = false,
}: {
  etat: EtatRythme | null
  choisi: Rythme | null
  onChoisir: (r: Rythme) => void
  disabled?: boolean
}) {
  const t = useT()
  return (
    <div className="rythmes" role="radiogroup" aria-label={t('rythme.aria')}>
      {RYTHMES.map((r) => {
        const n = etat?.estimations[r]
        return (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={choisi === r}
            className={`rythme${choisi === r ? ' on' : ''}`}
            disabled={disabled}
            onClick={() => onChoisir(r)}
          >
            <span className="rythme-tete">
              <b>{t(`rythme.${r}.nom` as const)}</b>
              <span className="rythme-seuil">{t(`rythme.${r}.seuil` as const)}</span>
              <span className="rythme-coche" aria-hidden="true"><Check size={13} strokeWidth={3} /></span>
            </span>
            <span className="rythme-n">
              {n === undefined ? ' ' : n === 0 ? t('rythme.estimationZero') : n === 1 ? t('rythme.estimationUn') : t('rythme.estimationPlus', { n })}
            </span>
            <span className="rythme-desc">{t(`rythme.${r}.desc` as const)}</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * L'adresse où arrivent les mails (lot 3). Une adresse pro, c'est une fuite :
 * on peut en donner une autre, confirmée par un lien envoyé à celle-ci.
 */
export function ChoixAdresse({ etat, onChange }: { etat: EtatRythme; onChange: () => void }) {
  const t = useT()
  const toast = useToast()
  const [ouvert, setOuvert] = useState(false)
  const [email, setEmail] = useState('')
  return (
    <div className="cfg-sous">
      <span>
        {t('adresse.actuelle', { email: etat.email ?? '' })}{' '}
        {!ouvert && (
          <button type="button" className="linkbtn" onClick={() => setOuvert(true)}>
            {t('adresse.changer')}
          </button>
        )}
      </span>
      {etat.enAttente && <EtatConfirmation etat={etat} onChange={onChange} />}
      {ouvert && (
        <span className="adresse-form">
          <input
            className="input-field"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('adresse.placeholder')}
            aria-label={t('adresse.placeholder')}
          />
          <ActionButton
            size="sm"
            variant="primary"
            disabled={!email.includes('@')}
            onClick={async () => {
              const r = await post<{ issue: 'compte' | 'en_attente'; envoye: boolean }>('/api/radar/adresse', { email })
              toast(r.issue === 'compte' ? t('adresse.compteToast') : r.envoye ? t('adresse.lienToast', { email }) : t('adresse.echecToast'), r.envoye ? undefined : 'err')
              setOuvert(false)
              setEmail('')
              onChange()
            }}
          >
            {t('adresse.envoyer')}
          </ActionButton>
        </span>
      )}
      <span className="field-help">{t('adresse.aide')}</span>
    </div>
  )
}


interface StatutAdresse {
  enAttente: string | null
  envoi: { email: string; le: string; ok: boolean; erreur: string | null } | null
  livraison: { statut: 'envoye' | 'livre' | 'differe' | 'refuse' | 'inconnu'; raison: string | null }
}

/**
 * Une adresse en attente de confirmation, dite en entier : où partent encore
 * les mails, si le lien est parti, ce que le serveur d'en face en a fait. Un
 * lien bloqué chez un employeur laissait croire que rien ne marchait.
 */
function EtatConfirmation({ etat, onChange }: { etat: EtatRythme; onChange: () => void }) {
  const t = useT()
  const toast = useToast()
  const statut = useData(() => get<StatutAdresse>('/api/radar/adresse/statut'), [etat.enAttente])
  const s = statut.data
  const quand = s?.envoi ? dateHeure(s.envoi.le) : null
  const envoi = !s
    ? null
    : s.envoi && !s.envoi.ok
      ? t('adresse.statut.pasParti', { erreur: s.envoi.erreur ?? '' })
      : s.livraison.statut === 'refuse'
        ? t('adresse.statut.refuse', { raison: s.livraison.raison ?? '' })
        : s.livraison.statut === 'livre'
          ? t('adresse.statut.livre')
          : s.livraison.statut === 'differe'
            ? t('adresse.statut.differe')
            : s.livraison.statut === 'envoye'
              ? t('adresse.statut.envoye')
              : null
  const probleme = !!s && ((s.envoi && !s.envoi.ok) || s.livraison.statut === 'refuse')
  async function demander(email: string) {
    const r = await post<{ issue: 'compte' | 'en_attente'; envoye: boolean }>('/api/radar/adresse', { email })
    toast(r.issue === 'compte' ? t('adresse.compteToast') : r.envoye ? t('adresse.lienToast', { email }) : t('adresse.echecToast'), r.issue === 'compte' || r.envoye ? undefined : 'err')
    onChange()
    statut.reload()
  }
  return (
    <span className="adresse-attente">
      <span>{t('adresse.attente', { attente: etat.enAttente ?? '' })}</span>
      {quand && <span className="field-help">{t('adresse.statut.lienLe', { quand })}</span>}
      {envoi && <span className={probleme ? 'field-help cfg-danger' : 'field-help'}>{envoi}</span>}
      {!probleme && s?.livraison.statut === 'livre' && <span className="field-help">{t('adresse.statut.pasRecu')}</span>}
      <span className="adresse-form">
        <ActionButton size="sm" onClick={() => demander(etat.enAttente ?? '')}>{t('adresse.renvoyer')}</ActionButton>
        {etat.emailCompte && (
          <button type="button" className="linkbtn" onClick={() => demander(etat.emailCompte ?? '')}>{t('common.cancel')}</button>
        )}
      </span>
    </span>
  )
}
